/* eslint-disable react/require-default-props */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { toast } from 'react-toastify';
import { FaPlus } from 'react-icons/fa';

import Modal from '../../../../../components/Modal/Modal';
import {
   GhostButton,
   DangerButton,
   SmallButton,
} from '../../../../../styles/ui/Button';
import { TableWrapper } from '../../../../../styles/table/TableWrapper';
import { Table } from '../../../../../styles/table/Table';
import { deleteDependent } from '../../../../../services/hr/recruitment/dependentsApi';
import Accordion from '../../components/Accordion';
import { DEPENDENT_RELATIONSHIP_LABEL } from '../../constants';
import { formatCpf, formatDateBr } from '../../utils/format';
import { recruitmentErrorMessage } from '../../utils/apiError';
import { InlineHint } from '../styles';
import DependentFormModal from './DependentFormModal';

// ---------------------------------------------------------------------------
// Dependentes (filhos) do candidato — ACCORDION (redesign 2026-07-14) com meta
// "Nenhum cadastrado" / "N dependente(s)". Dirigem os documentos
// por-dependente do checklist (idade define quais). Excluir é barrado pelo
// backend (422) quando o dependente já tem documento coletado. Toda mutação →
// onChanged() (o pai recarrega dependentes E documentos juntos).
// ---------------------------------------------------------------------------

export default function DependentsCard({
   candidateId,
   dependents = [],
   canManage,
   isReadOnly,
   onChanged,
}) {
   const [formTarget, setFormTarget] = useState(null); // null=fechado, {}=criar
   const [deleteTarget, setDeleteTarget] = useState(null);
   const [isDeleting, setIsDeleting] = useState(false);

   const canAct = canManage && !isReadOnly;

   const handleConfirmDelete = async () => {
      if (!deleteTarget || isDeleting) return;
      setIsDeleting(true);
      try {
         await deleteDependent(candidateId, deleteTarget.id);
         toast.success('Dependente excluído.');
         setDeleteTarget(null);
         onChanged?.();
      } catch (err) {
         toast.error(
            recruitmentErrorMessage(
               err,
               'Não foi possível excluir o dependente.',
            ),
         );
         setDeleteTarget(null);
      } finally {
         setIsDeleting(false);
      }
   };

   const accordionMeta =
      dependents.length === 0
         ? 'Nenhum cadastrado'
         : `${dependents.length} dependente${dependents.length > 1 ? 's' : ''}`;

   return (
      <Accordion title="Dependentes" meta={accordionMeta}>
         {dependents.length === 0 ? (
            <InlineHint style={{ margin: '16px 0 14px' }}>
               Nenhum dependente cadastrado. Dependentes adicionam documentos
               próprios ao checklist, conforme a idade.
            </InlineHint>
         ) : (
            <TableWrapper style={{ marginTop: 16 }}>
               <Table>
                  <thead>
                     <tr>
                        <th>Nome</th>
                        <th>Nascimento</th>
                        <th>Idade</th>
                        <th>CPF</th>
                        <th>Parentesco</th>
                        {canAct && <th>Ações</th>}
                     </tr>
                  </thead>
                  <tbody>
                     {dependents.map((dependent) => (
                        <tr key={dependent.id}>
                           <td>{dependent.full_name}</td>
                           <td>{formatDateBr(dependent.birth_date)}</td>
                           <td>
                              {dependent.age != null
                                 ? `${dependent.age} anos`
                                 : '—'}
                           </td>
                           <td>{formatCpf(dependent.cpf)}</td>
                           <td>
                              {DEPENDENT_RELATIONSHIP_LABEL[
                                 dependent.relationship
                              ] ?? '—'}
                           </td>
                           {canAct && (
                              <td style={{ whiteSpace: 'nowrap' }}>
                                 <SmallButton
                                    type="button"
                                    onClick={() => setFormTarget(dependent)}
                                    style={{ marginRight: 8 }}
                                 >
                                    Editar
                                 </SmallButton>
                                 <SmallButton
                                    type="button"
                                    $intent="danger"
                                    onClick={() => setDeleteTarget(dependent)}
                                 >
                                    Excluir
                                 </SmallButton>
                              </td>
                           )}
                        </tr>
                     ))}
                  </tbody>
               </Table>
            </TableWrapper>
         )}

         {canAct && (
            <div style={{ marginTop: 12 }}>
               <SmallButton type="button" onClick={() => setFormTarget({})}>
                  <FaPlus size={11} /> Novo dependente
               </SmallButton>
            </div>
         )}

         <DependentFormModal
            isOpen={Boolean(formTarget)}
            onClose={() => setFormTarget(null)}
            candidateId={candidateId}
            dependent={formTarget?.id ? formTarget : null}
            onSaved={onChanged}
         />

         <Modal
            isOpen={Boolean(deleteTarget)}
            onClose={() => setDeleteTarget(null)}
            title="Excluir dependente"
            maxWidth="440px"
            footer={
               <>
                  <GhostButton
                     type="button"
                     onClick={() => setDeleteTarget(null)}
                     disabled={isDeleting}
                  >
                     Cancelar
                  </GhostButton>
                  <DangerButton
                     type="button"
                     onClick={handleConfirmDelete}
                     disabled={isDeleting}
                  >
                     {isDeleting ? 'Excluindo…' : 'Excluir dependente'}
                  </DangerButton>
               </>
            }
         >
            <p style={{ margin: 0, fontSize: '0.9rem' }}>
               Tem certeza que deseja excluir{' '}
               <strong>{deleteTarget?.full_name}</strong>? Dependentes com
               documentos já coletados no checklist não podem ser excluídos.
            </p>
         </Modal>
      </Accordion>
   );
}

DependentsCard.propTypes = {
   candidateId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
   dependents: PropTypes.arrayOf(PropTypes.shape({})),
   canManage: PropTypes.bool,
   isReadOnly: PropTypes.bool,
   onChanged: PropTypes.func,
};
